<template>
  <div
    :class="['card', 'white', 'mb-4', 'tc-packing-box', isValid ? 'tc-packing-box--valid' : 'tc-packing-box--invalid']"
    :id="`box-${box.id}`"
  >
    <div class="card-body">
      <h2 class="d-flex justify-content-between align-items-center">
        <div>
          <span class="font-weight-bold">Box-{{ box.boxIndex + 1 }}</span>
          <small v-if="box.shippingMethod" class="ml-2 font-size-md text-muted">
            {{ box.shippingMethod }}
          </small>
          <small v-else class="ml-2 font-size-md text-muted font-italic">
            {{ t('features.input_packing.carrier_pending') }}
          </small>
        </div>
        <button class="btn btn-light text-danger" @click.prevent="confirmDelete"
                aria-label="Delete this box">
          <i class="ri-close-line"></i>
        </button>
      </h2>
      <div v-if="!isValid" class="tc-packing-box__alert alert alert-danger py-2 px-3 mb-3" role="alert">
        <div class="font-weight-bold">{{ invalidReasonText }}</div>
        <div class="small">{{ invalidSolutionText }}</div>
      </div>
      <div class="form-row mb-2">
        <div class="col-sm-5" v-if="decoratedMethods && decoratedMethods.length">
          <label :for="`carrier-${box.id}`">
            {{ t('features.input_packing.shipping_method') }}
          </label>
          <select
            :id="`carrier-${box.id}`"
            class="form-control"
            v-model="box.shippingMethodId"
            @change="emitUpdateShippingMethod"
          >
            <option
              v-for="opt in decoratedMethods"
              :key="opt.id"
              :value="opt.id"
            >
              {{ opt.label }}<span v-if="opt.id === box.expectedShippingMethodId"> (default)</span>
            </option>
          </select>
        </div>
        <div class="col-sm-3">
          <label :for="`size-${box.id}`">{{ t('features.input_packing.box_size') }}</label>
          <select :id="`size-${box.id}`" class="form-control" v-model="localBoxSize" @change="emitUpdateSize">
            <option
              v-for="size in availableBoxSizes"
              :key="size.value"
              :value="size.value"
            >{{ size.label }}</option>
          </select>
          <small v-if="boxSizeAutoAdjusted" class="text-warning d-block mt-1">
            {{ t('features.input_packing.box_size_auto_adjusted') }}
          </small>
        </div>
        <div class="col-sm-4">
          <label :for="`weight-${box.id}`">{{ t('features.input_packing.box_weight') }}</label>
          <div class="input-group">
            <input type="text" :id="`weight-${box.id}`" class="form-control" v-model="localWeight"
                   @input="emitUpdateWeight" />
            <div class="input-group-append">
              <span class="input-group-text">g</span>
            </div>
          </div>
        </div>
      </div>
      <ul class="list-group" @dragover.prevent @drop.prevent="onDrop">
        <WonPrize
          v-for="(prize, index) in box.wonPrizes"
          :key="prize.id"
          :won-prize="prize"
          :box-id="box.id"
          :seq="index + 1"
          @delete-item="$emit('delete-item', { boxId: box.id, itemId: prize.id })"
          @drop-item="onDropPosition"
        />
        <li
          v-if="box.wonPrizes.length === 0"
          class="list-group-item text-muted text-center tc-packing-box__placeholder"
        >
          {{ t('features.input_packing.drop_prize_here') }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import WonPrize from './won-prize.vue'
import { mapStores } from 'pinia'
import { useTokyoCatchInputPackingStore } from '../../stores/tokyoCatchInputPackingStore'

export default {
  inject: ['i18n'],
  components: { WonPrize },
  props: {
    box: { type: Object, required: true }
  },
  data() {
    return {
      t: (key, options) => this.i18n.t(key, options),
      localBoxSize: this.box.boxSize ?? null,
      localWeight: this.box.weight ?? null,
      // Set briefly when the carrier-change watcher swaps the box size to a
      // value supported by the new carrier; reset on the next operator edit.
      boxSizeAutoAdjusted: false
    }
  },
  computed: {
    ...mapStores(useTokyoCatchInputPackingStore),
    availableBoxSizes() {
      // Master list is supplied by the server from TokyoCatchOrderBox::BOX_OPTIONS_BY_SIZE
      // (Ruby model constant, the authoritative source). Labels resolve through i18n so
      // EN gets English wording and JP keeps the kanji codes the warehouse team uses.
      const CODES = (this.inputPackingStore && this.inputPackingStore.boxSizeOptions) || []
      const ALL_SIZES = CODES.map(code => ({
        value: code,
        label: this.t('features.input_packing.box_size_labels.' + code, { default: code })
      }))

      // If a carrier is selected, restrict the dropdown to that carrier's supported sizes.
      // Otherwise show the full master list. Always include the currently-selected
      // value so the operator can see (and re-pick) when it falls outside the new carrier's range.
      const carrierSizes = (this.selectedMethod && this.selectedMethod.tokyo_catch_box_sizes) || null
      if (!carrierSizes || !carrierSizes.length) return ALL_SIZES

      const allowed = ALL_SIZES.filter(s => carrierSizes.includes(s.value))
      const current = this.localBoxSize || this.box.boxSize
      if (current && !carrierSizes.includes(current)) {
        const stale = ALL_SIZES.find(s => s.value === current)
        if (stale) allowed.unshift({ ...stale, label: `${stale.label} (unsupported)` })
      }
      return allowed
    },
    isValid() {
      return this.invalidReasonKey === null
    },
    selectedMethod() {
      const selectedId = this.box.shippingMethodId
      if (!selectedId) return null
      return (this.box.shippingMethodOptions || []).find((m) => m.id === selectedId) || null
    },
    invalidReasonKey() {
      // Returns the i18n reason key for the current invalid state, or null when valid.
      // Surfaced one at a time in priority order so the operator fixes the most
      // blocking problem first; remaining issues appear as they are resolved.
      if (!this.box.wonPrizes || this.box.wonPrizes.length === 0) return 'prize_missing'
      if (this.weightInvalid) return 'weight_missing'
      if (!this.selectedMethod) return 'none_selected'
      return this.computeReason(this.selectedMethod)
    },
    invalidReasonText() {
      const key = this.invalidReasonKey
      if (!key) return ''
      if (key === 'none_selected' || key === 'prize_missing' || key === 'weight_missing') {
        return this.t('features.tokyocatch_orders.shipping_method_solution.' + key)
      }
      return this.t(
        'features.tokyocatch_orders.shipping_method_reason.' + key,
        this.reasonI18nParams(this.selectedMethod, key)
      )
    },
    invalidSolutionText() {
      const key = this.invalidReasonKey
      if (!key) return ''
      return this.t(
        'features.tokyocatch_orders.shipping_method_solution.' + key,
        this.reasonI18nParams(this.selectedMethod || {}, key)
      )
    },
    decoratedMethods() {
      if (!this.box.shippingMethodOptions || !Array.isArray(this.box.shippingMethodOptions)) {
        return []
      }

      return this.box.shippingMethodOptions.map((m) => {
        const sizes = m.tokyo_catch_box_sizes || []
        const sizesLabel = sizes.length ? ` (${sizes.join('/')})` : ''
        const reason = this.computeReason(m)
        const eligible = reason === null
        // Reason text is intentionally NOT appended to the option label — the inline
        // alert under the box title surfaces the reason + suggested fix instead.
        return { ...m, eligible, reason, label: `${m.name}${sizesLabel}` }
      })
    },
    weightInvalid() {
      const w = this.localWeight
      if (w === null || w === undefined || w === '') return false
      const n = Number(w)
      return Number.isNaN(n) || n <= 0
    }
  },
  watch: {
    'box.boxSize'(newVal) {
      this.localBoxSize = newVal
    },
    'box.weight'(newVal) {
      this.localWeight = newVal
    },
    'box.shippingMethodId'(newId) {
      // When the operator picks a new carrier and the current box size falls
      // outside that carrier's supported list, auto-pick the carrier's first
      // supported size so the box does not stay stuck on an unsupported value.
      if (!newId) return
      const method = (this.box.shippingMethodOptions || []).find(m => m.id === newId)
      const supported = method && method.tokyo_catch_box_sizes
      if (!supported || !supported.length) return
      const current = this.localBoxSize || this.box.boxSize
      if (current && supported.includes(current)) return
      const nextSize = supported[0]
      this.localBoxSize = nextSize
      this.box.boxSize = nextSize
      this.boxSizeAutoAdjusted = true
      this.$emit('update-size', { boxId: this.box.id, size: nextSize })
    }
  },
  mounted() {
    // localBoxSize and localWeight are now initialized in data()
  },
  methods: {
    computeReason(method) {
      // Recompute after edits; server-provided split rules use the order's destination.
      const currentSize = this.localBoxSize || this.box.boxSize
      const currentWeight = parseInt(this.localWeight || this.box.weight || 0, 10)

      // 1. box_size_unsupported
      if (method.tokyo_catch_box_sizes && !method.tokyo_catch_box_sizes.includes(currentSize)) {
        return 'box_size_unsupported'
      }

      const rules = method.split_fee_rules
      if (rules != null) {
        if (rules.length !== 2) return 'fee_schedule_missing'

        // Blank input preserves an existing measurement on save. New/unmeasured
        // boxes use current prize grams plus the selected packaging weight.
        const estimatedWeight = (this.box.wonPrizes || []).reduce((sum, prize) => sum + Number(prize.grams || 0), 0) +
          Number((method.box_weights_by_size || {})[currentSize] || 0)
        const actualWeight = this.localWeight !== null && this.localWeight !== undefined && this.localWeight !== ''
          ? Number(this.localWeight)
          : Number(this.box.persistedWeight || estimatedWeight)

        for (const rule of rules) {
          const bands = rule.weight_bands || []
          if (!bands.length) return 'fee_schedule_missing'
          const volume = Number((rule.volumetric_grams_by_size || {})[currentSize] || 0)
          const grams = rule.mile_type === 'first_mile' ? Math.max(actualWeight, volume) : actualWeight
          if (grams > Math.max(...bands.map(band => band.max_grams))) return 'weight_exceeds_cap'
          if (!bands.some(band => grams >= band.min_grams && grams <= band.max_grams)) return 'fee_schedule_missing'
        }
      } else {
        const maxGrams = method.max_grams
        if (!maxGrams) return 'fee_schedule_missing'
        if (currentWeight > maxGrams) return 'weight_exceeds_cap'
      }

      // 3. fda_blocked (ECMS only)
      if (method.carrier_kind === 'ecms' && this.box.fda_entry_required && method.fda_unshippable) {
        return 'fda_blocked'
      }

      // 4. variant_mismatch
      const country = this.box.country || 'US'
      const isFDA = this.box.fda_entry_required

      if (method.carrier_kind === 'ocs') {
        // OCS: code 'ocs_fda' only valid for US+FDA; code 'ocs' only valid for non-US OR (US AND !FDA)
        if (method.code === 'ocs_fda') {
          // FDA-only carrier on a non-FDA box → distinct reason so we can render different copy.
          if (!(country === 'US' && isFDA)) return 'fda_only_carrier'
        } else if (method.code === 'ocs') {
          if (country === 'US' && isFDA) return 'variant_mismatch'
        }
      }

      if (method.carrier_kind === 'ecms') {
        // ECMS: code 'ecms_food' only valid for US+FDA; code 'ecms' only valid for non-US OR (US AND !FDA)
        if (method.code === 'ecms_food') {
          if (!(country === 'US' && isFDA)) return 'fda_only_carrier'
        } else if (method.code === 'ecms') {
          if (country === 'US' && isFDA) return 'variant_mismatch'
        }
      }

      return null
    },
    reasonI18nParams(method, reason) {
      const currentSize = this.localBoxSize || this.box.boxSize
      return {
        size: currentSize,
        country: this.box.country || 'US'
      }
    },
    emitUpdateSize(event) {
      const size = this.localBoxSize
      this.box.boxSize = size
      this.boxSizeAutoAdjusted = false
      this.$emit('update-size', { boxId: this.box.id, size: size })
    },
    emitUpdateWeight(event) {
      const weight = this.localWeight
      this.box.weight = weight
      this.$emit('update-weight', { boxId: this.box.id, weight: weight })
    },
    emitUpdateShippingMethod(event) {
      const value = event.target.value
      this.$emit('update-shipping-method', { boxId: this.box.id, shippingMethodId: value ? Number(value) : null })
    },
    onDrop(event) {
      const data = event.dataTransfer.getData('text/plain')
      if (!data) return
      const { itemId, fromBoxId } = JSON.parse(data)
      this.$emit('move-item', { itemId, fromBoxId, toBoxId: this.box.id })
    },
    onDropPosition({ itemId, fromBoxId, toBoxId, targetItemId }) {
      this.$emit('move-item', { itemId, fromBoxId, toBoxId, targetItemId })
    },
    confirmDelete() {
      if (confirm(this.t('features.input_packing.confirm_delete'))) {
        this.$emit('delete-box', this.box.id)
      }
    }
  }
}
</script>

<style scoped>
.tc-packing-box {
  border-width: 2px;
  border-style: solid;
  transition: border-color 150ms ease-in-out;
}
.tc-packing-box--valid {
  border-color: #28a745;
}
.tc-packing-box--invalid {
  border-color: #dc3545;
}
.tc-packing-box__placeholder {
  min-height: 60px;
  border: 1px dashed #adb5bd;
  background-color: #f8f9fa;
}
</style>
